import { UNITS_OF_MEASURE } from '../constants/unitsOfMeasure';
import { ThemeVariable, VariableOption } from './types';
import { labelVariables } from './variables';
import { parseDisplayDate } from '../utils/parseDisplayDate';
import { isTacPercentKey } from '../utils/tac';

// ============================================================================
// Date Format Presets
// ============================================================================

/**
 * Available date format presets for the UI dropdown.
 */
export const DATE_FORMAT_PRESETS = [
  { label: 'MM/DD/YYYY', value: 'MM/DD/YYYY' },
  { label: 'MM-DD-YYYY', value: 'MM-DD-YYYY' },
  { label: 'MM/DD/YY', value: 'MM/DD/YY' },
  { label: 'YYYY-MM-DD', value: 'YYYY-MM-DD' },
  { label: 'DD/MM/YYYY', value: 'DD/MM/YYYY' },
  { label: 'Month DD, YYYY', value: 'MMMM DD, YYYY' },
] as const;

export const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';

export const resolveConditionalTextSource = (
  conditionText: unknown,
  baseText: unknown,
  variableKey: string | undefined,
): string => {
  if (typeof conditionText === 'string') {
    return conditionText.trim().length ? conditionText : '';
  }

  // `baseText` may originate from legacy/corrupted/migrated template JSON, so it is
  // not guaranteed to actually be a string at runtime despite the TS type.
  const safeBaseText = typeof baseText === 'string' ? baseText : '';

  return safeBaseText.trim().length
    ? safeBaseText
    : variableKey
      ? `{{${variableKey}}}`
      : '';
};

/**
 * Check if a variable key represents a date variable.
 * Uses case-insensitive check for 'Date' in the key name for built-in variables,
 * and checks the explicit `type` property for theme variables.
 */
export const isDateVariable = (
  variableKey: string | undefined,
  themeVariables?: ThemeVariable[],
): boolean => {
  if (!variableKey) return false;

  // Check built-in label variables by regex (they have 'date' in their key name)
  if (/date/i.test(variableKey)) return true;

  // Check theme variables by their explicit type
  if (themeVariables) {
    const themeMatch = themeVariables.find((v) => v.key === variableKey);
    if (themeMatch && themeMatch.type === 'date') return true;
  }

  return false;
};

// ============================================================================
// Number Format Presets
// ============================================================================

/**
 * Available decimal place presets for the UI dropdown.
 * -1 = auto (no formatting / pass-through).
 */
export const NUMBER_DECIMAL_PRESETS = [
  { label: 'Auto (no formatting)', value: -1 },
  { label: '0', value: 0 },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
] as const;

export const NUMBER_METHOD_PRESETS = [
  { label: 'Round', value: 'round' },
  { label: 'Truncate', value: 'truncate' },
] as const;

export const DEFAULT_NUMBER_DECIMAL_PLACES = 2;
export const DEFAULT_NUMBER_METHOD: 'round' | 'truncate' = 'round';

export const isNumberVariable = (
  variableKey: string | undefined,
  themeVariables?: ThemeVariable[],
): boolean => {
  if (!variableKey) return false;

  //check labelVariables
  if (isTacPercentKey(variableKey)) return true;
  const labelMatch = labelVariables.find((v) => v.key === variableKey);
  if (labelMatch && labelMatch.type === 'number') return true;

  //check theme variables
  if (themeVariables) {
    const themeMatch = themeVariables.find((v) => v.key === variableKey);
    if (themeMatch && themeMatch.type === 'number') return true;
  }

  return false;
};

//format options
export type NumberFormatEntry = {
  decimalPlaces: number;
  method: 'round' | 'truncate';
};

//format a numeric value with the specified decimal places and method.
//returns the original string representation if the value cannot be parsed as a number,
//or if decimalPlaces is -1 (auto / no formatting).

export const formatNumber = (
  value: unknown,
  decimalPlaces: number,
  method: 'round' | 'truncate',
): string => {
  //-1 means auto / no formatting
  if (decimalPlaces === -1) return stringifyValue(value);

  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return stringifyValue(value);

  if (method === 'truncate') {
    // Preserve the value if legacy/imported formatting metadata is invalid.
    if (!Number.isInteger(decimalPlaces) || decimalPlaces < 0 || decimalPlaces > 100) {
      return stringifyValue(value);
    }

    // Work with decimal digits: 1.15 * 100 is 114.99999999999999 in binary
    // floating point. Scaling and Math.trunc would incorrectly remove a digit.
    const [coefficient = '0', exponent = '0'] = Math.abs(num).toString().split('e');
    const [whole = '0', fraction = ''] = coefficient.split('.');
    const digits = whole + fraction;
    const decimalIndex = whole.length + Number(exponent);
    const integerDigits = decimalIndex > 0
      ? digits.slice(0, decimalIndex).padEnd(decimalIndex, '0')
      : '0';
    const fractionalDigits = decimalIndex < 0
      ? '0'.repeat(-decimalIndex) + digits
      : digits.slice(decimalIndex);
    const keptFraction = fractionalDigits.slice(0, decimalPlaces).padEnd(decimalPlaces, '0');
    // Truncation is toward zero; retain the existing unsigned-zero output.
    const sign = num < 0 && /[1-9]/.test(integerDigits + keptFraction) ? '-' : '';

    return decimalPlaces > 0
      ? `${sign}${integerDigits}.${keptFraction}`
      : `${sign}${integerDigits}`;
  }

  //default is to round
  return num.toFixed(decimalPlaces);
};

// ============================================================================
// Date Formatting Utilities
// ============================================================================

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Format a Date object using a format string.
 * Supports: YYYY, YY, MMMM (full month), MM, M, DD, D
 */
const formatDateString = (date: Date, format: string): string => {
  const pad = (n: number): string => n.toString().padStart(2, '0');

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  const replacements: Record<string, string> = {
    YYYY: year.toString(),
    YY: year.toString().slice(-2),
    MMMM: MONTH_NAMES[date.getMonth()],
    MM: pad(month),
    M: month.toString(),
    DD: pad(day),
    D: day.toString(),
  };

  return format.replace(/YYYY|MMMM|MM|YY|DD|M|D/g, (token) => replacements[token]);
};

/**
 * Format a date value using the specified format string.
 * Returns the original value if parsing fails.
 */
export const formatDate = (value: any, format: string = DEFAULT_DATE_FORMAT): string => {
  if (!value) return '';
  const date = parseDisplayDate(value);
  if (!date) return String(value);
  return formatDateString(date, format);
};

// ============================================================================
// Raw Variable Resolution for Legacy Templates
// ============================================================================

/**
 * Known variable prefixes that need special resolution in legacy templates.
 * These prefixes correspond to raw data sources that may not be flattened
 * into the main data object after templateFn evaluation.
 */
export const RAW_VARIABLE_PREFIXES = [
  'theme.',
  'coa.',
  'job.',
  'product.',
  'metrc.',
  'topTerpenes.',
  'topCannabinoids.',
] as const;

/**
 * Context containing raw data sources for second-pass variable resolution.
 * Each key corresponds to a prefix (without the dot) and contains the
 * nested data that can be traversed with valueForKeyPath.
 */
export type RawDataContext = {
  tacReportedCoa?: Record<string, unknown>;
  tacReportedFunctionData?: Record<string, unknown>;
  coa?: Record<string, any>;
  job?: Record<string, any>;
  product?: Record<string, any>;
  theme?: Record<string, any>;
  metrc?: Record<string, any>;
  topTerpenes?: any[];
  topCannabinoids?: any[];
};

// ============================================================================
// Utility Functions
// ============================================================================

const stringifyValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (
    typeof value === 'object' &&
    typeof (value as any).toString === 'function' &&
    (value as any).toString !== Object.prototype.toString
  ) {
    return (value as any).toString();
  }
  try {
    return JSON.stringify(value);
  } catch (err) {
    console.warn('Failed to stringify value for text replacement', err);
    return String(value);
  }
};

/**
 * Normalize a keypath by converting bracket notation to dot notation.
 * e.g., 'terpene_list[0]' => 'terpene_list.0'
 *       'data[0].name' => 'data.0.name'
 *       'nested[0][1]' => 'nested.0.1'
 */
const normalizeKeyPath = (keyPath: string): string => {
  return keyPath.replace(/\[(\d+)\]/g, '.$1');
};

const potencyUnitPath = /^(coa\.)?(ingredients|terpenes)\.([^.]+)\.weight\.(perUnit|srv)Uom$/;
const potencyMeasurementPath = /^(coa\.)?(ingredients|terpenes)\.[^.]+\.(percent|weight\.(amount|ml|perUnit|srv|pkg|percent|weightMgG))$/;

/** A cleared COA measurement cannot acquire a zero through numeric coercion. */
const formatNumberForKeyPath = (key: string, value: unknown, format: NumberFormatEntry): string => {
  if ((potencyMeasurementPath.test(normalizeKeyPath(key)) || isTacPercentKey(key))
    && (value === null || (typeof value === 'string' && value.trim() === ''))) return '';
  return formatNumber(value, format.decimalPlaces, format.method);
};

const potencyMassUnits = new Set([...UNITS_OF_MEASURE.filter(unit => unit.QuantityType === 'WeightBased').map(unit => unit.Abbreviation.toLowerCase()), 'µg', 'μg']);
const potencyUnitBases = new Set([...UNITS_OF_MEASURE.map(unit => unit.Abbreviation.toLowerCase()), 'unit', 'srv', 'serving', 'pkg', 'package']);

/**
 * Get value at a keypath, handling both arrays and objects.
 * Supports both dot notation and bracket notation for array access.
 * Matches valueForKeyPath from typed-json-transform.
 *
 * Examples:
 *   valueForKeyPath('terpene_list.0', { terpene_list: ['a', 'b'] }) => 'a'
 *   valueForKeyPath('terpene_list[0]', { terpene_list: ['a', 'b'] }) => 'a'
 *   valueForKeyPath('coa.lab.name', { coa: { lab: { name: 'Test' } } }) => 'Test'
 *   valueForKeyPath('data[0].name', { data: [{ name: 'First' }] }) => 'First'
 *   valueForKeyPath('0', ['a', 'b']) => 'a'
 */
export const valueForKeyPath = (keyPath: string, input: Record<string, any> | any[]): any => {
  if (!input || !keyPath) return undefined;

  // Normalize bracket notation to dot notation
  const normalizedPath = normalizeKeyPath(keyPath);

  // The saved binding must resolve the current package, not capture the unit of
  // the package selected while authoring. These are rendering-only values: the
  // source measurement, unit and compound-level fallback remain untouched.
  const potencyUnit = normalizedPath.match(potencyUnitPath);
  if (potencyUnit) {
    const [, prefix = '', family, key, field] = potencyUnit;
    const compound = valueForKeyPath(`${prefix}${family}.${key}`, input);
    // Allow the ordinary second-pass raw COA resolver when fullData has no coa.
    if (compound === undefined) return undefined;
    if (compound === null || typeof compound !== 'object' || Array.isArray(compound)) return '';
    const weight = compound.weight;
    if (weight === null || typeof weight !== 'object' || Array.isArray(weight)) return '';
    const amount = weight[field];
    if ((typeof amount !== 'number' && typeof amount !== 'string') ||
        (typeof amount === 'string' && amount.trim() === '') || !Number.isFinite(Number(amount))) return '';
    const unit = weight.unit ?? compound.uom;
    if (typeof unit !== 'string' || unit.trim() === '') return '';
    const savedUnit = unit.trim();
    const parts = savedUnit.split('/').map(part => part.trim().toLowerCase());
    const numerator = parts[0] || '';
    const recognized = parts.length <= 2 && potencyMassUnits.has(numerator)
      && (parts.length === 1 || potencyUnitBases.has(parts[1] || ''));
    return recognized
      ? `${numerator}/${field === 'perUnit' ? 'unit' : 'serving'}`
      : savedUnit;
  }

  let current: any = input;
  const keys = normalizedPath.split('.');

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (current === null || current === undefined) {
      return undefined;
    }

    if (Array.isArray(current)) {
      // For arrays, parse the key as an integer index
      const index = parseInt(key, 10);
      if (isNaN(index)) {
        return undefined;
      }
      current = current[index];
    } else if (typeof current === 'object') {
      current = current[key];
    } else {
      return undefined;
    }
  }

  return current;
};

const tokenRegex = /{{\s*([^{}]+?)\s*}}/g;

type ExamplesMap = Record<string, string | undefined>;

export const parseExampleObject = (example: string): Record<string, any> | null => {
  if (!example || !example.trim().startsWith('{')) return null;

  try {
    const jsonLike = example.replace(/([{,]\s*)([a-zA-Z_]\w*)\s*:/g, '$1"$2":').replace(/'/g, '"');

    return JSON.parse(jsonLike);
  } catch {
    return null;
  }
};

export const getDrillPaths = (
  obj: Record<string, any>,
  prefix: string = '',
): { path: string; value: any }[] => {
  const paths: { path: string; value: any }[] = [];

  for (const [key, value] of Object.entries(obj)) {
    const fullPath = prefix ? `${prefix}.${key}` : key;

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      paths.push(...getDrillPaths(value, fullPath));
    } else {
      paths.push({ path: fullPath, value });
    }
  }

  return paths;
};

export const buildExamplesMap = (variables: VariableOption[]): ExamplesMap => {
  const acc: ExamplesMap = {};

  for (const variable of variables) {
    acc[variable.key] = variable.example;

    if (variable.type === 'object' && variable.example) {
      const parsed = parseExampleObject(variable.example);
      if (parsed) {
        const drillPaths = getDrillPaths(parsed);
        for (const { path, value } of drillPaths) {
          const fullKey = `${variable.key}.${path}`;
          acc[fullKey] = typeof value === 'string' ? value : String(value);
        }
      }
    }
  }

  return acc;
};

// ============================================================================
// Raw Variable Resolution
// ============================================================================

/**
 * Second-pass resolution for variables with known prefixes.
 * Handles cases where variables like {{theme.netWeight}} or {{coa.batch}}
 * weren't resolved by the first pass because the data structure uses
 * flat keys instead of nested objects.
 *
 * For legacy templates, the templateFn may return computed values without
 * the prefix (e.g., `netWeight` instead of `theme.netWeight`), but the
 * template text still references the prefixed version.
 *
 * This function looks up the value in the appropriate raw data source
 * based on the prefix.
 */
export const resolveRawVariables = (text: string, rawData: RawDataContext): string => {
  if (!text || !text.includes('{{')) return text;

  return text.replace(tokenRegex, (_match, rawKey) => {
    const key = String(rawKey || '').trim();
    if (!key) return '';

    // Check if key starts with a known prefix
    for (const prefix of RAW_VARIABLE_PREFIXES) {
      if (key.startsWith(prefix)) {
        const dataKey = prefix.slice(0, -1) as keyof RawDataContext; // Remove trailing dot
        const source = rawData[dataKey];
        if (source) {
          const subPath = key.slice(prefix.length); // e.g., "netWeight" from "theme.netWeight"
          const value = valueForKeyPath(subPath, source);
          if (value !== undefined) {
            // An unavailable derived unit is resolved empty, not an authoring placeholder.
            if (value === '' && potencyUnitPath.test(normalizeKeyPath(key))) return '';
            const rendered = stringifyValue(value);
            // Only return if we got a non-empty value
            if (rendered !== '') {
              return rendered;
            }
          }
        }
        break; // Only one prefix can match
      }
    }

    // Keep unresolved
    return `{{${key}}}`;
  });
};

// ============================================================================
// Main Text Rendering
// ============================================================================

/**
 * Options for date and number formatting in renderTextWithData.
 */
export type DateFormatOptions = {
  /** Map of variable keys to their date format strings */
  dateFormats?: Record<string, string>;
  /** Map of variable keys to their number format settings */
  numberFormats?: Record<string, NumberFormatEntry>;
  /** When false (final PDF), unresolved variables return empty string instead of {{key}} */
  isPreview?: boolean;
  /** Theme variables for date/number type detection */
  themeVariables?: ThemeVariable[];
};

/**
 * Render text by replacing {{variable}} tokens with values from data.
 *
 * @param text - The text containing {{variable}} tokens
 * @param data - Primary data object for variable resolution
 * @param examples - Fallback example values for variables
 * @param rawData - Optional raw data context for second-pass resolution of prefixed variables
 * @param options - Optional formatting options (e.g., dateFormats)
 */
export const renderTextWithData = (
  text: string,
  data?: Record<string, any>,
  examples?: ExamplesMap,
  rawData?: RawDataContext,
  options?: DateFormatOptions,
): string => {
  if (!text) return '';

  let result = text.replace(tokenRegex, (_match, rawKey) => {
    const key = String(rawKey || '').trim();
    if (!key) return '';

    if (data) {
      // Use valueForKeyPath to support dot notation like "terpene_list.0"
      const value = valueForKeyPath(key, data);
      if (value !== undefined) {
        // An unavailable derived unit is resolved empty, not an authoring placeholder.
        if (value === '' && potencyUnitPath.test(normalizeKeyPath(key))) return '';
        // Check if this variable has a date format specified
        const dateFormat = options?.dateFormats?.[key];
        if (dateFormat && isDateVariable(key, options?.themeVariables)) {
          const formatted = formatDate(value, dateFormat);
          // For final PDF (isPreview === false), show empty string; otherwise show placeholder
          return formatted === '' ? (options?.isPreview === false ? '' : `{{${key}}}`) : formatted;
        }
        const numberFormat = options?.numberFormats?.[key];
        if (numberFormat) {
          return formatNumberForKeyPath(key, value, numberFormat);
        }
        const rendered = stringifyValue(value);
        // For final PDF (isPreview === false), show empty string; otherwise show placeholder
        return rendered === '' ? (options?.isPreview === false ? '' : `{{${key}}}`) : rendered;
      }
    }

    const example = examples?.[key];
    if (example && example.trim()) {
      // Apply date formatting to example values as well
      const dateFormat = options?.dateFormats?.[key];
      if (dateFormat && isDateVariable(key, options?.themeVariables)) {
        return formatDate(example, dateFormat);
      }
      const numberFormat = options?.numberFormats?.[key];
      if (numberFormat) {
        return formatNumberForKeyPath(key, example, numberFormat);
      }
      return example;
    }

    return `{{${key}}}`;
  });

  // Second pass: resolve any remaining prefixed variables using raw data sources
  // This handles legacy templates where variables like {{theme.netWeight}} or {{coa.batch}}
  // weren't resolved because the templateFn returns unprefixed keys
  if (rawData && result.includes('{{')) {
    result = resolveRawVariablesWithDateFormat(result, rawData, options);
  }

  // A derived unit has no authoring placeholder of its own. Let both primary
  // and raw package data resolve first, then hide any unavailable unit token.
  // Ordinary unresolved variables retain their existing preview placeholders.
  if (result.includes('{{')) {
    result = result.replace(tokenRegex, (match, rawKey) =>
      potencyUnitPath.test(normalizeKeyPath(String(rawKey || '').trim())) ? '' : match,
    );
  }

  if (options?.isPreview === false && result.includes('{{')) {
    result = result.replace(tokenRegex, '');
  }

  return result;
};

/**
 * Extended version of resolveRawVariables that supports date and number formatting.
 */
const resolveRawVariablesWithDateFormat = (
  text: string,
  rawData: RawDataContext,
  options?: DateFormatOptions,
): string => {
  if (!text || !text.includes('{{')) return text;

  return text.replace(tokenRegex, (_match, rawKey) => {
    const key = String(rawKey || '').trim();
    if (!key) return '';

    // Check if key starts with a known prefix
    for (const prefix of RAW_VARIABLE_PREFIXES) {
      if (key.startsWith(prefix)) {
        const dataKey = prefix.slice(0, -1) as keyof RawDataContext; // Remove trailing dot
        const source = rawData[dataKey];
        if (source) {
          const subPath = key.slice(prefix.length); // e.g., "netWeight" from "theme.netWeight"
          const value = valueForKeyPath(subPath, source);
          if (value !== undefined) {
            // An unavailable derived unit is resolved empty, not an authoring placeholder.
            if (value === '' && potencyUnitPath.test(normalizeKeyPath(key))) return '';
            // Check if this variable has a date format specified
            const dateFormat = options?.dateFormats?.[key];
            if (dateFormat && isDateVariable(key, options?.themeVariables)) {
              const formatted = formatDate(value, dateFormat);
              if (formatted !== '') {
                return formatted;
              }
            }
            //check if this variable has a number format specified
            const numberFormat = options?.numberFormats?.[key];
            if (numberFormat) {
              return formatNumberForKeyPath(key, value, numberFormat);
            }
            const rendered = stringifyValue(value);
            // Only return if we got a non-empty value
            if (rendered !== '') {
              return rendered;
            }
          }
        }
        break; // Only one prefix can match
      }
    }

    // Keep unresolved - for final PDF (isPreview === false), show empty string; otherwise show placeholder
    return options?.isPreview === false ? '' : `{{${key}}}`;
  });
};

// ============================================================================
// Object Variable Drill-Down Utilities
// ============================================================================

/**
 * Extract all variable keys from text containing {{variable}} tokens.
 * Returns an array of unique variable keys found in the text.
 */
export const extractVariableKeys = (text: string): string[] => {
  if (!text) return [];
  const matches = text.matchAll(tokenRegex);
  const keys = [...matches].map((m) => m[1].trim());
  return [...new Set(keys)];
};

/**
 * Information about an object variable that can be drilled into.
 */
export interface ObjectVariableInfo {
  /** The variable key as it appears in the text, e.g., "coa.totals.cbd" */
  variableKey: string;
  /** Available drill-down paths with their example values */
  drillPaths: { path: string; value: any }[];
}
